'use client';
import React, { useEffect, useState } from 'react';
import { EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import ClassNames from 'embla-carousel-class-names';
import { Box } from '@chakra-ui/react';
import './styles.css';
import {
  NextButton,
  PrevButton,
  usePrevNextButtons,
} from '@/app/[locale]/_components/EmblaCarousel/ArrowButtons';

const OPTIONS: EmblaOptionsType = {
  loop: true,
  align: 'center',
};

export default function Carousel({ slides }: { slides: React.ReactNode[] }) {
  const [emblaRef, emblaAPI] = useEmblaCarousel(OPTIONS, [
    Autoplay({ delay: 5000 }),
    ClassNames(),
  ]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaAPI);

  useEffect(() => {
    if (!emblaAPI) return;

    // Define the select event handler
    const onSelect = () => {
      setSelectedIndex(emblaAPI.selectedScrollSnap());
    };

    // Register the event handler
    emblaAPI.on('select', onSelect);

    // Initialize with the correct index
    onSelect();

    // Clean up the event handler on unmount
    return () => {
      emblaAPI.off('select', onSelect);
    };
  }, [emblaAPI]);

  const handleDotClick = (index: number) => {
    if (emblaAPI) {
      emblaAPI.scrollTo(index);
    }
  };

  return (
    <Box
      as='section'
      className='embla desktop'
      style={
        {
          '--slide-size': '100%',
        } as React.CSSProperties
      }
      position='relative'
    >
      <Box className='embla__viewport' ref={emblaRef}>
        <div className='embla__container'>{slides}</div>
      </Box>
      {slides.length > 1 && (
        <Box display={{ base: 'none', lg: 'flex' }} className='embla__buttons'>
          <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
          <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
        </Box>
      )}
      {slides.length > 1 && (
        <Box
          position='absolute'
          bottom='4'
          left='50%'
          transform='translateX(-50%)'
          zIndex='1'
        >
          <div className='embla__dots'>
            {slides.map((_, index) => (
              <button
                key={index}
                className={`embla__dot ${
                  selectedIndex === index
                    ? 'embla__dot--active is-selected'
                    : ''
                }`}
                onClick={() => handleDotClick(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </Box>
      )}
    </Box>
  );
}
